// Function index:
//   WeaponKind / WEAPON_PARTS — overlay silhouettes drawn relative to the
//       body's weaponAnchor (local 0,0 = grip), currentColor only.
//   ICON_TO_WEAPON — item-icon target → weapon part (rides ICON_KEYWORDS so
//       equipped-item naming logic stays single-sourced with the icon map).
//   weaponForItemName(name) — equipped item name → WeaponKind | null.
//   RANGED_WEAPONS — parts that shoot projectile beats instead of lunging.

import { ICON_KEYWORDS } from '../../items/icon-map.js';
import type { PartDef } from './types.js';

export type WeaponKind = 'sword' | 'axe' | 'bow' | 'crossbow' | 'staff' | 'spear' | 'dagger' | 'mace' | 'hammer' | 'shield';

export const WEAPON_PARTS: Record<WeaponKind, PartDef> = {
  sword:    { width: 20, svg: '<line x1="0" y1="0" x2="14" y2="-14" stroke="currentColor" stroke-width="2.5"/><line x1="1" y1="-5" x2="6" y2="0" stroke="currentColor" stroke-width="2"/>' },
  axe:      { width: 20, svg: '<line x1="0" y1="0" x2="10" y2="-12" stroke="currentColor" stroke-width="2.5"/><path d="M10 -12 a7 7 0 0 1 4 10 Z" fill="currentColor"/>' },
  bow:      { width: 18, svg: '<path d="M2 -12 q12 12 0 24" fill="none" stroke="currentColor" stroke-width="2.5"/><line x1="2" y1="-12" x2="2" y2="12" stroke="currentColor" stroke-width="1.2"/>' },
  crossbow: { width: 18, svg: '<line x1="0" y1="0" x2="12" y2="0" stroke="currentColor" stroke-width="2.5"/><path d="M8 -7 q8 7 0 14" fill="none" stroke="currentColor" stroke-width="2"/>' },
  staff:    { width: 14, svg: '<line x1="2" y1="14" x2="2" y2="-18" stroke="currentColor" stroke-width="2.5"/><circle cx="2" cy="-20" r="3" fill="currentColor"/>' },
  spear:    { width: 22, svg: '<line x1="0" y1="6" x2="18" y2="-16" stroke="currentColor" stroke-width="2"/><path d="M18 -16 l4 -5 l-1 6 Z" fill="currentColor"/>' },
  dagger:   { width: 12, svg: '<line x1="0" y1="0" x2="8" y2="-8" stroke="currentColor" stroke-width="2"/>' },
  mace:     { width: 16, svg: '<line x1="0" y1="0" x2="9" y2="-10" stroke="currentColor" stroke-width="2.5"/><circle cx="10" cy="-11" r="3.5" fill="currentColor"/>' },
  hammer:   { width: 18, svg: '<line x1="0" y1="0" x2="10" y2="-11" stroke="currentColor" stroke-width="2.5"/><rect x="6" y="-16" width="9" height="6" rx="1" fill="currentColor" transform="rotate(-45 10 -11)"/>' },
  shield:   { width: 14, svg: '<path d="M-2 -6 a8 8 0 0 0 0 14 Z" fill="currentColor"/>' },
};

// Icon targets (icon-map.ts) that translate into a wieldable overlay. Icons
// with no martial meaning (potion, rope, …) map to nothing.
export const ICON_TO_WEAPON: Readonly<Record<string, WeaponKind>> = {
  sword: 'sword', dagger: 'dagger', axe: 'axe', pickaxe: 'axe',
  mace: 'mace', hammer: 'hammer', spear: 'spear', bow: 'bow',
  crossbow: 'crossbow', staff: 'staff', wand: 'staff', shield: 'shield',
};

export function weaponForItemName(name: string | null | undefined): WeaponKind | null {
  if (!name) return null;
  const n = name.toLowerCase();
  for (const [keywords, icon] of ICON_KEYWORDS) {
    if (keywords.some((k) => n.includes(k))) return ICON_TO_WEAPON[icon] ?? null;
  }
  return null;
}

export const RANGED_WEAPONS: ReadonlySet<WeaponKind> = new Set(['bow', 'crossbow']);
